import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { lesen, schreiben } from "@/lib/speicher";
import useHandy from "@/hooks/useHandy";
import { useAuth } from "@/context/AuthContext";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { spracheingabeVerfuegbar, spracheingabeStarten, vorlesen, vorlesenStop, vorlesenVerfuegbar } from "@/lib/sprache";
import { Sparkles, X, Send, Loader2, ArrowRight, Trash2, FileDown, Mic, Square, Volume2, VolumeX, Check, CircleCheck } from "lucide-react";

/**
 * Der Assistent - fragen, nachsehen lassen, erledigen.
 *
 * Er raet nicht, sondern fragt die Datenbank ueber Werkzeuge ab (Anrufe,
 * Aufgaben, Bestellungen, Termine, Kunden, Umsaetze); welche das waren,
 * steht unter jeder Antwort. Er kennt die Seite, auf der man gerade ist:
 * In einer Kundenakte heisst "was hat er zuletzt bestellt" diesen Kunden.
 *
 * Handeln tut er nie ungefragt: Er schlaegt vor (Aufgabe anlegen, Anruf
 * erledigen, uebernehmen), und ein Tipp auf den Vorschlag fuehrt es aus.
 *
 * Auf dem Handy ist er ein Blatt ueber der ganzen Seite, mit Mikrofon:
 * sprechen, loslassen, Antwort hoeren (lib/sprache.js). Am Rechner das
 * Fenster unten rechts wie bisher.
 */

const VERLAUF_MARKE = "assistent_verlauf";
const VORLESEN_MARKE = "assistent_vorlesen";

/** Vorschlaege, die zur Seite passen - der Assistent ist da, wo man arbeitet. */
function vorschlaegeFuer(pfad, kontext) {
  if (kontext?.customer_id) {
    return ["Was hat dieser Kunde zuletzt bestellt?", "Gab es Beschwerden von diesem Kunden?",
            "Wie hat sich sein Umsatz entwickelt?", "Lege eine Aufgabe für diesen Kunden an"];
  }
  if (pfad.startsWith("/telefonate")) {
    return ["Welche Rückrufe sind offen?", "Welche Anrufe hängen länger als einen Tag?",
            "Was wollte der letzte Anrufer?", "Gab es heute Reklamationen?"];
  }
  if (pfad.startsWith("/bestellungen") || pfad.startsWith("/bestellen")) {
    return ["Was wartet auf Freigabe?", "Was wurde diese Woche bestellt?",
            "Gibt es gescheiterte Bestellungen?", "Welche Bestellnummer hat die letzte Komet-Bestellung?"];
  }
  if (pfad.startsWith("/aufgaben")) {
    return ["Welche meiner Aufgaben sind überfällig?", "Was liegt im Pool ohne Person?",
            "Was ist heute fällig?", "Welche Aufgaben hat Lucy?"];
  }
  if (pfad.startsWith("/agenten")) {
    return ["Läuft gerade alles?", "Was muss ich bei den Agenten jetzt tun?", "Was kosten die Agenten im Monat?"];
  }
  if (pfad.startsWith("/werbung")) {
    return ["Welche Kampagne lief zuletzt und wie war die Klickquote?", "Wie viele Kunden sind je Land sendebereit?",
            "Wer hat in den letzten 30 Tagen auf einen Bestell-Link geklickt?", "Was fehlt noch bis zum ersten Versand?"];
  }
  if (pfad.startsWith("/wettbewerb/fakten")) {
    return ["Welche Fragen haben die Agenten offen?", "Welche Fakten sind gesichert?",
            "Wo fehlt noch ein Nachweis?", "Was wissen die Agenten über Zirkon?"];
  }
  if (pfad.startsWith("/wettbewerb/herkunft")) {
    return ["Aus welchen Ländern kamen diesen Monat Anfragen?", "Wie viele Neukunden kamen über KI-Assistenten?",
            "Welcher Weg bringt die meisten Neukunden?", "Wie viele Anfragen kamen aus Italien?"];
  }
  if (pfad.startsWith("/wettbewerb/offensive")) {
    return ["Welche Angriffe laufen gerade?", "Wer steht bei Fräszentrum vor uns?",
            "Welche Faktenfragen sind offen?", "Läuft die volle Variante oder die Sparvariante?"];
  }
  if (pfad.startsWith("/wettbewerb")) {
    return ["Bei welchen Artikeln können wir entspannt erhöhen?", "Wo ist CADdent billiger als wir?",
            "Auf welchem Platz stehen wir bei Google für Fräszentrum?", "Welche Vorschläge der Agenten sind offen?"];
  }
  if (pfad === "/tafel" || pfad === "/") {
    return ["Wie läuft es heute?", "Welche Rückrufe sind offen?",
            "Was wartet auf Freigabe?", "Welche Termine stehen heute an?"];
  }
  return ["Was ist diese Woche liegen geblieben?", "Welche kritischen Kunden haben den höchsten Umsatz?",
          "Gab es Beschwerden wegen der Passung?", "Warum sind Kunden abgewandert?"];
}

/** Was die Seite dem Assistenten mitgibt: Kunde, Gespraech, Reiter. */
function kontextAus(ort) {
  const k = {};
  const kunde = ort.pathname.match(/^\/kunden\/([^/]+)/);
  if (kunde) k.customer_id = kunde[1];
  const p = new URLSearchParams(ort.search);
  if (ort.pathname.startsWith("/telefonate") && p.get("id")) k.contact_id = p.get("id");
  if (p.get("kontakt")) k.contact_id = p.get("kontakt");
  if (ort.pathname.startsWith("/bestellen/")) k.lieferant = ort.pathname.split("/")[2];
  return k;
}

const AKTION_TEXT = {
  aufgabe_anlegen: (a) => `Aufgabe anlegen: ${a.titel}${a.faellig_am ? ` · fällig ${a.faellig_am.slice(8, 10)}.${a.faellig_am.slice(5, 7)}.` : ""}${a.verantwortlich ? ` · ${a.verantwortlich}` : ""}`,
  anruf_erledigt: () => "Anruf als erledigt markieren",
  anruf_uebernehmen: () => "Anruf übernehmen",
  aufgabe_erledigt: () => "Aufgabe als erledigt markieren",
  aufgabe_uebernehmen: () => "Aufgabe übernehmen",
};

function verlaufLesen() {
  try {
    const roh = sessionStorage.getItem(VERLAUF_MARKE);
    const v = roh ? JSON.parse(roh) : [];
    return Array.isArray(v) ? v.slice(-30) : [];
  } catch { return []; }
}

function verlaufMerken(v) {
  try { sessionStorage.setItem(VERLAUF_MARKE, JSON.stringify(v.slice(-30))); } catch { /* egal */ }
}

export default function Assistent() {
  const handy = useHandy();
  const { user } = useAuth();
  const navigate = useNavigate();
  const ort = useLocation();
  const me = user?.name || user?.email || "";

  const [offen, setOffen] = useState(false);
  const [verlauf, setVerlauf] = useState(verlaufLesen);
  const [eingabe, setEingabe] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [pdfLaeuft, setPdfLaeuft] = useState(null);
  const [aktionLaeuft, setAktionLaeuft] = useState(null);
  // Sprache: "aus" | "hoert" | "verarbeitet"; Zwischenstand waehrend des Sprechens
  const [hoert, setHoert] = useState("aus");
  const [zwischen, setZwischen] = useState("");
  const [vorlesenAn, setVorlesenAn] = useState(() => lesen(VORLESEN_MARKE, "1") === "1");
  const sprecher = useRef(null);
  const ende = useRef(null);
  const feld = useRef(null);

  const kontext = useMemo(() => kontextAus(ort), [ort]);
  const vorschlaege = useMemo(() => vorschlaegeFuer(ort.pathname, kontext), [ort.pathname, kontext]);
  const sprache = spracheingabeVerfuegbar();

  useEffect(() => { verlaufMerken(verlauf); }, [verlauf]);
  useEffect(() => { schreiben(VORLESEN_MARKE, vorlesenAn ? "1" : "0"); }, [vorlesenAn]);
  useEffect(() => {
    if (offen) setTimeout(() => ende.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [verlauf, offen, laeuft]);
  useEffect(() => () => { sprecher.current?.stop(); vorlesenStop(); }, []);

  const fragen = useCallback(async (text, { gesprochen = false } = {}) => {
    const frage = (text ?? eingabe).trim();
    if (!frage || laeuft) return;
    setEingabe("");
    setZwischen("");
    setVerlauf((v) => [...v, { rolle: "nutzer", text: frage, gesprochen }]);
    setLaeuft(true);
    try {
      const r = await api.post("/assistent", {
        frage,
        verlauf: verlauf.slice(-8).map(({ rolle, text: t }) => ({ rolle, text: t })),
        seite: ort.pathname + ort.search,
        kontext,
        sprache: gesprochen,
      });
      setVerlauf((v) => [...v, {
        rolle: "assistent",
        text: r.data.antwort,
        ansicht: r.data.ansicht,
        werkzeuge: r.data.verwendete_werkzeuge,
        aktionen: r.data.aktionen || [],
      }]);
      if (gesprochen && vorlesenAn) vorlesen(r.data.antwort);
    } catch (e) {
      setVerlauf((v) => [...v, {
        rolle: "assistent",
        text: e.response?.data?.detail || "Das hat nicht geklappt. Bitte noch einmal versuchen.",
        fehler: true,
      }]);
    } finally {
      setLaeuft(false);
    }
  }, [eingabe, laeuft, verlauf, ort.pathname, ort.search, kontext, vorlesenAn]);

  // ------------------------------------------------------------ Sprache
  const sprechen = useCallback(() => {
    if (hoert === "hoert") { sprecher.current?.stop(); return; }
    if (hoert === "verarbeitet" || laeuft) return;
    vorlesenStop();
    setZwischen("");
    sprecher.current = null;
    Promise.resolve(spracheingabeStarten({
      onZustand: setHoert,
      onZwischen: setZwischen,
      onFertig: (t) => {
        setZwischen("");
        if (t) fragen(t, { gesprochen: true });
        else toast("Nichts verstanden.", { description: "Bitte noch einmal sprechen, etwas näher am Mikrofon." });
      },
      onFehler: (grund) => { setHoert("aus"); toast.error("Spracheingabe", { description: grund }); },
    })).then((s) => { sprecher.current = s; });
  }, [hoert, laeuft, fragen]);

  // ------------------------------------------------------------ Aktionen
  const aktionAusfuehren = useCallback(async (index, ai) => {
    const a = verlauf[index]?.aktionen?.[ai];
    if (!a) return;
    setAktionLaeuft(`${index}:${ai}`);
    try {
      if (a.typ === "aufgabe_anlegen") {
        await api.post(`/customers/${a.customer_id}/followup`, {
          title: a.titel, due_date: a.faellig_am || null, owner: a.verantwortlich || null,
          art: a.art || "kundenservice", status: "Open", source: "assistent",
          description: a.beschreibung || "",
        });
      } else if (a.typ === "anruf_erledigt") {
        await api.patch(`/telefonate/${a.contact_id}`, { bearbeitung: "erledigt" });
      } else if (a.typ === "anruf_uebernehmen") {
        await api.patch(`/telefonate/${a.contact_id}`, { zustaendig: me, bearbeitung: "in Bearbeitung" });
      } else if (a.typ === "aufgabe_erledigt") {
        await api.put(`/followups/${a.followup_id}`, { status: "Done" });
      } else if (a.typ === "aufgabe_uebernehmen") {
        await api.put(`/followups/${a.followup_id}`, { owner: me, status: "In Progress" });
      }
      toast.success("Erledigt.", { description: AKTION_TEXT[a.typ]?.(a) });
      setVerlauf((v) => v.map((n, i) => i !== index ? n
        : { ...n, aktionen: n.aktionen.map((x, j) => j === ai ? { ...x, erledigt: true } : x) }));
    } catch (e) {
      toast.error("Das ging nicht.", { description: e.response?.data?.detail || "Bitte in der Ansicht selbst erledigen." });
    } finally {
      setAktionLaeuft(null);
    }
  }, [verlauf, me]);

  /** Laedt die Auswertung als PDF herunter - zum Ablegen oder Weitergeben. */
  const protokollLaden = async (index) => {
    const antwort = verlauf[index];
    const frage = [...verlauf.slice(0, index)].reverse().find((n) => n.rolle === "nutzer")?.text || "";
    setPdfLaeuft(index);
    try {
      const r = await api.post("/assistent/protokoll", { frage, antwort: antwort.text, quellen: antwort.werkzeuge || [] },
                               { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "auswertung.pdf";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setVerlauf((v) => [...v, { rolle: "assistent", fehler: true, text: "Das Protokoll konnte nicht erstellt werden." }]);
    } finally {
      setPdfLaeuft(null);
    }
  };

  const schliessen = () => { sprecher.current?.stop(); vorlesenStop(); setOffen(false); };
  const hinspringen = (ziel) => { schliessen(); navigate(ziel); };

  // -------------------------------------------------------------- Inhalt
  const inhalt = (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[12rem]">
        {verlauf.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Frag mich nach Anrufen, Aufgaben, Bestellungen, Terminen oder Kunden.
              Ich sehe in den Daten nach, statt zu raten{sprache ? " - auch per Sprache" : ""}.
            </p>
            <div className="space-y-1.5">
              {vorschlaege.map((v) => (
                <button key={v} onClick={() => fragen(v)} data-testid="assistent-vorschlag"
                        className="w-full text-left text-sm text-slate-700 rounded-md border border-slate-200 px-3 py-2.5
                                   hover:border-slate-900 hover:bg-slate-50 active:bg-slate-100 transition">
                  {v}
                </button>
              ))}
            </div>
          </div>
        )}

        {verlauf.map((n, i) => (
          <div key={i} className={n.rolle === "nutzer" ? "flex justify-end" : ""}>
            {n.rolle === "nutzer" ? (
              <div className="bg-slate-900 text-white rounded-lg rounded-br-sm px-3 py-2 text-sm max-w-[85%]">
                {n.gesprochen && <Mic className="inline h-3 w-3 mr-1 -mt-0.5 opacity-70" />}{n.text}
              </div>
            ) : (
              <div className="space-y-2">
                <div className={`rounded-lg rounded-bl-sm px-3 py-2 text-sm whitespace-pre-line ${
                  n.fehler ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-800"}`}>
                  {n.text}
                </div>
                {(n.aktionen || []).length > 0 && (
                  <div className="space-y-1.5" data-testid="assistent-aktionen">
                    {n.aktionen.map((a, ai) => (
                      <button key={ai} type="button" disabled={a.erledigt || aktionLaeuft !== null}
                              onClick={() => aktionAusfuehren(i, ai)} data-testid={`assistent-aktion-${a.typ}`}
                              className={`w-full text-left rounded-lg border-2 px-3 py-2.5 text-sm font-medium flex items-center gap-2.5 transition ${
                                a.erledigt ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                : "border-slate-900 bg-white text-slate-900 hover:bg-slate-50 active:bg-slate-100"}`}>
                        {a.erledigt ? <CircleCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                          : aktionLaeuft === `${i}:${ai}` ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                          : <Check className="h-4 w-4 shrink-0" />}
                        <span className="flex-1">{AKTION_TEXT[a.typ]?.(a) || a.typ}</span>
                        {!a.erledigt && <span className="text-[11px] text-slate-500 shrink-0">tippen = ausführen</span>}
                      </button>
                    ))}
                  </div>
                )}
                {!n.fehler && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {n.ansicht && (
                      <button type="button" onClick={() => hinspringen(n.ansicht)} data-testid="assistent-ansicht"
                              className="h-9 md:h-8 px-3 rounded-md border border-slate-300 bg-white text-sm font-medium text-slate-800 inline-flex items-center gap-1.5 hover:bg-slate-50">
                        Ansicht öffnen <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button" disabled={pdfLaeuft === i} onClick={() => protokollLaden(i)} data-testid="assistent-pdf"
                            className="h-9 md:h-8 px-2.5 rounded-md text-sm text-slate-600 inline-flex items-center gap-1.5 hover:bg-slate-100">
                      {pdfLaeuft === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Als PDF
                    </button>
                    {vorlesenVerfuegbar() && (
                      <button type="button" onClick={() => vorlesen(n.text)} title="Vorlesen" aria-label="Vorlesen"
                              className="h-9 md:h-8 w-9 md:w-8 rounded-md text-slate-500 inline-flex items-center justify-center hover:bg-slate-100">
                        <Volume2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
                {n.werkzeuge?.length > 0 && (
                  <div className="text-[11px] text-slate-400">nachgesehen in: {n.werkzeuge.join(", ")}</div>
                )}
              </div>
            )}
          </div>
        ))}

        {laeuft && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> sieht nach …
          </div>
        )}
        <div ref={ende} />
      </div>

      <div className="border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {hoert !== "aus" && (
          <div className="mb-2 flex items-center gap-2 text-sm" data-testid="assistent-hoert">
            <span className={`h-2.5 w-2.5 rounded-full ${hoert === "hoert" ? "bg-red-500 animate-pulse" : "bg-amber-500"}`} />
            <span className="text-slate-700 flex-1 truncate">
              {hoert === "hoert" ? (zwischen || "Ich höre zu … sprich einfach.") : "Wird in Text umgewandelt …"}
            </span>
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); fragen(); }} className="flex items-center gap-2">
          <input ref={feld} value={eingabe} onChange={(e) => setEingabe(e.target.value)}
                 placeholder={hoert === "hoert" ? "… sprich, ich höre zu" : "Frage eingeben …"}
                 disabled={laeuft || hoert !== "aus"} data-testid="assistent-eingabe"
                 className="h-11 md:h-9 flex-1 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-base md:text-sm
                            text-slate-900 placeholder:text-slate-400 outline-none focus:border-slate-400 disabled:bg-slate-50" />
          {sprache && (
            <button type="button" onClick={sprechen} data-testid="assistent-mikro" aria-label={hoert === "hoert" ? "Aufnahme beenden" : "Sprechen"}
                    disabled={laeuft || hoert === "verarbeitet"}
                    className={`h-11 w-11 md:h-9 md:w-9 rounded-full flex items-center justify-center shrink-0 transition ${
                      hoert === "hoert" ? "bg-red-600 text-white animate-pulse"
                      : hoert === "verarbeitet" ? "bg-amber-500 text-white"
                      : "bg-slate-900 text-white hover:bg-slate-700"} disabled:opacity-50`}>
              {hoert === "hoert" ? <Square className="h-4 w-4" /> : hoert === "verarbeitet" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-5 w-5 md:h-4 md:w-4" />}
            </button>
          )}
          <button type="submit" disabled={laeuft || !eingabe.trim() || hoert !== "aus"} aria-label="Senden"
                  className="h-11 w-11 md:h-9 md:w-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0 disabled:opacity-40">
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </>
  );

  const kopf = (
    <div className="flex items-center justify-between px-4 py-2.5 border-b">
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles className="h-4 w-4 text-slate-500 shrink-0" />
        <span className="text-sm font-medium text-slate-900">Assistent</span>
        {kontext.customer_id && <span className="text-[11px] text-slate-400 truncate">· kennt diese Kundenakte</span>}
        {kontext.contact_id && !kontext.customer_id && <span className="text-[11px] text-slate-400 truncate">· kennt dieses Gespräch</span>}
      </div>
      <div className="flex items-center gap-0.5">
        {vorlesenVerfuegbar() && (
          <button onClick={() => { setVorlesenAn((a) => !a); vorlesenStop(); }} data-testid="assistent-vorlesen"
                  title={vorlesenAn ? "Antworten auf Sprachfragen werden vorgelesen" : "Vorlesen aus"}
                  className={`p-2 rounded ${vorlesenAn ? "text-slate-700" : "text-slate-300"}`}>
            {vorlesenAn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
        )}
        {verlauf.length > 0 && (
          <button onClick={() => { setVerlauf([]); vorlesenStop(); }} title="Verlauf leeren" className="p-2 text-slate-400 hover:text-slate-700 rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        <button onClick={schliessen} aria-label="Schließen" className="p-2 text-slate-400 hover:text-slate-700 rounded">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  if (!offen) {
    return (
      <button onClick={() => setOffen(true)} data-testid="assistent-oeffnen" aria-label="Assistent öffnen"
              className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] md:bottom-5 right-3 md:right-5 z-20 h-11 w-11 md:h-12 md:w-12 rounded-full bg-slate-900
                         text-white shadow-lg hover:bg-slate-800 transition flex items-center justify-center">
        <Sparkles className="h-5 w-5" />
      </button>
    );
  }

  // Handy: ein Blatt ueber der ganzen Seite - Platz fuer Antworten, das
  // Mikrofon unten beim Daumen. Rechner: das Fenster unten rechts.
  if (handy) {
    return (
      <Drawer open={offen} onOpenChange={(o) => { if (!o) schliessen(); }}>
        <DrawerContent className="h-[92vh] max-h-[92vh] flex flex-col" data-testid="assistent">
          <DrawerTitle className="sr-only">Assistent</DrawerTitle>
          {kopf}
          {inhalt}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-20 w-[min(28rem,calc(100vw-2.5rem))] rounded-lg border border-slate-200 bg-white shadow-2xl
                    flex flex-col max-h-[min(42rem,calc(100vh-3rem))]" data-testid="assistent">
      {kopf}
      {inhalt}
    </div>
  );
}
